// User support surface: the signed-in user's tickets with a "New ticket"
// dialog (POST create → navigate to the new thread). Cursor "Load more"
// follows the same accumulator pattern as DiscoveryTable / AdminUsers.

import { TicketStatusBadge } from '@/components/support/TicketStatusBadge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useCreateTicket, useTickets } from '@/hooks/useSupport';
import { extractApiError } from '@/lib/api';
import type { TicketListItem } from '@/lib/support';
import { LifeBuoy, Loader2, Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function Support() {
  const navigate = useNavigate();

  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [accumulated, setAccumulated] = React.useState<TicketListItem[]>([]);

  const list = useTickets(cursor);

  React.useEffect(() => {
    if (!list.data) return;
    if (cursor === undefined) {
      setAccumulated(list.data.items);
    } else {
      setAccumulated((prev) => {
        const seen = new Set(prev.map((t) => t.id));
        return [...prev, ...list.data.items.filter((t) => !seen.has(t.id))];
      });
    }
  }, [list.data, cursor]);

  const create = useCreateTicket();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');

  function openDialog() {
    setSubject('');
    setBody('');
    setDialogOpen(true);
  }

  function submit() {
    const s = subject.trim();
    const b = body.trim();
    if (!s) {
      toast.error('A subject is required.');
      return;
    }
    if (!b) {
      toast.error('Please describe your issue.');
      return;
    }
    create.mutate(
      { subject: s, body: b },
      {
        onSuccess: (created) => {
          setDialogOpen(false);
          // POST returns the full thread shape {ticket, messages}, not a flat
          // ticket — pluck the nested id, not `created.id` (which is undefined
          // and produced /support/tickets/undefined -> "Ticket not found").
          navigate(`/support/tickets/${created.ticket.id}`);
        },
        onError: (err) => {
          toast.error(extractApiError(err)?.message ?? 'Could not create ticket.');
        },
      },
    );
  }

  const showInitialLoader = list.isLoading && accumulated.length === 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Support</h1>
          <p className="text-sm text-muted-foreground">
            Open a ticket and our team will get back to you here.
          </p>
        </div>
        <Button onClick={openDialog}>
          <Plus className="mr-2 h-4 w-4" /> New ticket
        </Button>
      </header>

      {list.isError ? (
        <div className="rounded-md border bg-destructive/5 p-4 text-sm text-destructive">
          {extractApiError(list.error)?.message ?? 'Failed to load tickets — please refresh.'}
        </div>
      ) : showInitialLoader ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : accumulated.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed py-16 text-center">
          <LifeBuoy className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            You have no support tickets yet.
          </p>
          <Button variant="outline" onClick={openDialog}>
            <Plus className="mr-2 h-4 w-4" /> New ticket
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[1%] whitespace-nowrap">#</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead>Last activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accumulated.map((t) => (
                  <TableRow
                    key={t.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/support/tickets/${t.id}`)}
                  >
                    <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">#{t.number}</TableCell>
                    <TableCell className="max-w-md">
                      <span className="font-medium hover:underline">{t.subject}</span>
                    </TableCell>
                    <TableCell>
                      <TicketStatusBadge status={t.status} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{t.message_count}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {fmtDate(t.last_message_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {list.data?.next_cursor ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={list.isFetching}
                onClick={() => setCursor(list.data?.next_cursor ?? undefined)}
              >
                {list.isFetching ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && !create.isPending && setDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New support ticket</DialogTitle>
            <DialogDescription>
              Tell us what's going on. We'll reply in this thread and email you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="ticket-subject">Subject</Label>
              <Input
                id="ticket-subject"
                placeholder="Short summary of the issue"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={create.isPending}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticket-body">Description</Label>
              <Textarea
                id="ticket-body"
                placeholder="Describe the problem, what you expected, and any steps to reproduce."
                className="min-h-[140px]"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={create.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button onClick={submit} disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
